import React, { useState } from "react";
import { ArrowUpDown } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { CardHeader, CardTitle } from "./ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Trash2, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import fetchWithAuth from "../fetchWithAuth";
import { useBulkRowSelection, SelectionCheckbox, BulkDeleteBar } from "./BulkRowSelection";

export default function QueryTable({ projectId, queries = [], onChange, onBulkChange }) {
  const [newQuery, setNewQuery] = useState("");
  const [error, setError] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [queryToDelete, setQueryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [localQueries, setLocalQueries] = useState(queries);
  const selection = useBulkRowSelection(localQueries, setLocalQueries, { projectId, resource: "queries", onChange: onBulkChange });
  const [sorting, setSorting] = useState({ column: "queryText", direction: "asc" });

  // Обновляем localQueries при изменении props.queries
  React.useEffect(() => {
    setLocalQueries(queries);
  }, [queries]);

  // Сортировка
  const handleSort = (column) => {
    setSorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === "asc" ? "desc" : "asc"
    }));
  };

  const getSortedQueries = () => {
    const sorted = [...localQueries];
    if (sorting.column === "queryText") {
      sorted.sort((a, b) => {
        if (!a.queryText) return 1;
        if (!b.queryText) return -1;
        if (a.queryText < b.queryText) return sorting.direction === "asc" ? -1 : 1;
        if (a.queryText > b.queryText) return sorting.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  };

  // Пакетное добавление: каждая строка — отдельный запрос
  const handleAdd = async (e) => {
    e.preventDefault();
    setError("");
    const lines = newQuery
      .split("\n")
      .map(q => q.trim())
      .filter(q => q.length > 0);
    if (lines.length === 0) {
      setError("Введите хотя бы один запрос");
      return;
    }
    try {
      const res = await fetchWithAuth(`/api/project/${projectId}/queries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ queryText: lines.length === 1 ? lines[0] : lines }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || "Ошибка при добавлении");
        return;
      }
      
      // Получаем новые данные и обновляем локальное состояние вместо вызова onChange
      const updatedDataRes = await fetchWithAuth(`/api/project/${projectId}`);
      const updatedData = await updatedDataRes.json();
      setLocalQueries(updatedData.queries || []);
      
      setNewQuery("");
      // Не вызываем onChange, чтобы избежать перезагрузки страницы
      // onChange && onChange();
    } catch {
      setError("Ошибка при добавлении");
    }
  };

  const openDeleteDialog = (query) => {
    setQueryToDelete(query);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = () => {
    if (!queryToDelete) return;
    
    // Обновляем локальное состояние без перезагрузки всей страницы
    setLocalQueries(prevQueries => prevQueries.filter(q => q.id !== queryToDelete.id));
    
    // Закрываем диалог
    setIsDeleteDialogOpen(false);
    
    // Выполняем удаление на сервере
    fetchWithAuth(`/api/project/${projectId}/queries/${queryToDelete.id}`, {
      method: "DELETE",
    })
    .then(response => {
      if (!response.ok) {
        // Проверяем Content-Type ответа
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return response.json().then(data => {
            throw new Error(data.error || "Ошибка при удалении");
          });
        } else {
          throw new Error("Ошибка при удалении");
        }
      }
      
      // Не уведомляем родительский компонент об изменении, чтобы не перезагружать страницу
      // onChange && onChange();
      setQueryToDelete(null);
    })
    .catch(error => {
      console.error("Ошибка при удалении:", error);
      setError(error.message || "Ошибка при удалении");
    });
  };

  return (
    <div className="space-y-4">
      <CardHeader className="px-0 pt-0">
        <CardTitle>Список запросов</CardTitle>
      </CardHeader>
      
      <form onSubmit={handleAdd} className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <Textarea
              placeholder="Новые запросы (каждая строка — отдельный запрос)"
              value={newQuery}
              onChange={e => setNewQuery(e.target.value)}
              className="min-h-[100px]"
            />
          </div>
          <Button type="submit" className="self-start">Добавить</Button>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}
      </form>
      
      <BulkDeleteBar selection={selection} label="Запросы" />
      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[44px]"><SelectionCheckbox checked={selection.all} partial={selection.partial} onChange={selection.toggleAll} disabled={selection.busy || !localQueries.length} label="Выбрать все запросы" /></TableHead>
              <TableHead className="w-[50px]"></TableHead>
              <TableHead className="w-[50px]">#</TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  onClick={() => handleSort("queryText")}
                  className="flex items-center gap-1 font-medium"
                >
                  Текст запроса
                  <ArrowUpDown className="h-4 w-4" />
                </Button>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {getSortedQueries().map((q, idx) => (
              <TableRow key={q.id}>
                <TableCell><SelectionCheckbox checked={selection.selected.has(q.id)} onChange={() => selection.toggle(q.id)} disabled={selection.busy} label={`Выбрать запрос ${q.queryText}`} /></TableCell>
                <TableCell className="w-[50px]">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                    onClick={() => openDeleteDialog(q)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
                <TableCell className="w-[50px]">{idx + 1}</TableCell>
                <TableCell>{q.queryText}</TableCell>
              </TableRow>
            ))}
            {localQueries.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-4 text-gray-500">
                  Нет запросов
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Диалог подтверждения удаления */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px] pointer-events-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Подтверждение удаления
            </DialogTitle>
            <DialogDescription>
              Вы уверены, что хотите удалить этот запрос?
              {queryToDelete && (
                <div className="mt-2 p-2 bg-gray-50 rounded border text-sm break-all">
                  {queryToDelete.queryText}
                </div>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsDeleteDialogOpen(false)}
            >
              Отмена
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? "Удаление..." : "Удалить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
